import { ServerField } from '@uplift/ui-v2';
import { useFormikContext } from 'formik';
import React, { Fragment, useMemo } from 'react';
import { SectionContainerContent, SectionContainerDivider } from './section-container.component';
import { useClinicalNotesContext } from '../clinical-notes.context';
import { getYesNoFieldKeyFromFieldKey, getYesNoFromBooleanValue } from '../helpers';
import { useSectionStyles } from '../sections/styles';
import { ClinicalNotesDynamicFormKeyType, ClinicalNotesFormType } from '../types';
import { YesNoRadioControl } from './yes-no-radio-control.component';
import { useSessionClinicalNoteQuery } from '@uplift/queries';


export type MapSessionFormFieldsProps = {
  namespace: ClinicalNotesDynamicFormKeyType;
  formFieldIds?: string[];
  sectioned?: boolean;
  targetFieldAndRender?: {
    field: string;
    ignoreSectioning: boolean;
    renderInput: (input: React.ReactNode) => React.ReactNode;
  };
};


export const MapSessionFormFields: React.VFC<MapSessionFormFieldsProps> = ({
  namespace,
  sectioned = false,
  targetFieldAndRender
}) => {
  const { classes } = useSectionStyles();
  const { state: { sessionResource: { session } } } = useClinicalNotesContext();
  const { data: clinicalNotes } = useSessionClinicalNoteQuery(session.id, { suspense: true });
  const formik = useFormikContext<ClinicalNotesFormType>();

  const fields = useMemo(() => clinicalNotes.notes[namespace].formFields, [ namespace, clinicalNotes ]);

  const withToggleVisibility = (input: React.ReactNode, config: { visibleOnValue: boolean; label: string }, id: string) => {
    const yesNoKey = getYesNoFieldKeyFromFieldKey(id, namespace);
    const isToggled = formik.values[yesNoKey] === getYesNoFromBooleanValue(config.visibleOnValue);

    let yesNoInput = (
      <YesNoRadioControl
        baseName=""
        fullName={yesNoKey}
        label={config.label}
      />
    );

    if (sectioned) {
      yesNoInput = withSectioned(yesNoInput, !isToggled);
    }

    return (
      <Fragment>
        {yesNoInput}
        {isToggled && input}
      </Fragment>
    );
  };

  const withSectioned = (input: React.ReactNode, showDivider: boolean, greyBackground?: boolean) => (
    <Fragment>
      <SectionContainerContent className={greyBackground && classes.greyBackground}>
        {input}
      </SectionContainerContent>
      {showDivider && <SectionContainerDivider />}
    </Fragment>
  );

  return (
    <Fragment>
      {fields.map((field) => {
        const { id, toggleVisibility } = field;
        const name = `${namespace}.${id}`;
        let input: React.ReactNode = <ServerField color="secondary" key={field.id} name={name} field={field} />;
        const hasTargetFieldAndRender = targetFieldAndRender && targetFieldAndRender.field === id;

        if (!hasTargetFieldAndRender && sectioned) {
          input = withSectioned(input, !toggleVisibility, !!toggleVisibility);
        }
        if (!hasTargetFieldAndRender && toggleVisibility) {
          input = withToggleVisibility(input, toggleVisibility, id);
        }
        if (hasTargetFieldAndRender) {
          input = targetFieldAndRender.renderInput(input);
        }

        return (
          <Fragment key={id}>
            {input}
          </Fragment>
        );
      })}
    </Fragment>
  );
};
